import React from 'react'
import PropTypes from 'prop-types'
import { Alert, CloseAlt } from '@ticketswap/comets'
import flattenDepth from 'lodash/flattenDepth'
import styled from '@emotion/styled'
import {
  useDialog,
  DialogWindow,
  DialogHeader,
  DialogAdornment,
  DialogBody,
  Toggle,
  space,
  fontWeight,
  H6,
} from '@ticketswap/solar'
import { decodeGlobalId } from '~/utils/id'

const ListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${fontWeight.semiBold};

  &:not(:first-of-type) {
    margin-top: ${space[8]};
  }
`

const StyledH6 = styled(H6)`
  margin-top: ${space[16]};
`

function TicketAlertsDialog({ t, alerts, eventId, onClose, ...props }) {
  const groupedTypes = props.types.groups?.map(
    group => group?.eventTypes?.map(type => ({ node: type })) || []
  )
  const initialTypes = [
    ...(props?.types?.entrance?.length > 0 ? props.types.entrance : []),
    ...(props?.types?.nonEntrance?.length > 0 ? props.types.nonEntrance : []),
    ...flattenDepth(groupedTypes),
  ].map(({ node }) => ({
    id: node.id,
    title: node.title,
    active: getOn(node),
  }))
  const types = React.useRef(initialTypes)
  const { hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: on => {
      if (!on) {
        const payload = types?.current?.map(node => ({
          eventTypeId: node.id,
          active: node.active,
        }))
        onClose(payload)
      }
    },
  })

  /**
   * Determines whether the toggle is initially on or off.
   */
  function getOn(node) {
    // Filters
    const eventType = ({ eventType }) => eventType?.id === node.id
    const eventWithoutEventType = ({ event, eventType }) =>
      event.id === eventId && !eventType

    // Check if alerts array contains the current event-type.
    if (alerts.filter(eventType).length > 0) {
      return true
    }

    // Check if alerts array contains the current event without a
    // type, which implies that alerts are enabled on event level.
    if (alerts.filter(eventWithoutEventType).length > 0) {
      return true
    }

    return false
  }

  function handleToggle(on, node) {
    // Find the correct type in the array and change
    // its `active` value.
    let newTypes = types.current
    for (const i in newTypes) {
      if (newTypes[i].id === node.id) {
        newTypes[i].active = on
        break // Stop loop once we found it.
      }
    }
    types.current = newTypes

    // Pass data for tracking-purposes.
    props.onToggle(on, 'EDIT_TYPES', {
      eventTypeId: decodeGlobalId(node.id),
      eventTypeName: node.title,
    })
  }

  return (
    <>
      {props.children({ getToggleProps })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {t('event-eventtype:ticketAlerts.title')}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <DialogBody>
          <H6>{t('event-eventtype:types.entrance')}</H6>
          <ul>
            {props.types?.entrance?.map(({ node }) => (
              <ListItem key={node.id}>
                <span>{node.title}</span>
                <Toggle
                  defaultOn={getOn(node)}
                  adornment={<Alert size={16} />}
                  onToggle={on => handleToggle(on, node)}
                />
              </ListItem>
            ))}
          </ul>

          {props.types?.groups?.length > 0 ? (
            <ul>
              {props.types.groups.map(group => (
                <li key={group.id}>
                  <StyledH6>{group.name}</StyledH6>
                  <ul>
                    {group.eventTypes?.map(node => (
                      <ListItem key={node.id}>
                        <span>{node.title}</span>
                        <Toggle
                          defaultOn={getOn(node)}
                          adornment={<Alert size={16} />}
                          onToggle={on => handleToggle(on, node)}
                        />
                      </ListItem>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : null}

          {props.types?.nonEntrance?.length > 0 ? (
            <>
              <StyledH6>{t('event-eventtype:types.nonEntrance')}</StyledH6>
              <ul>
                {props.types.nonEntrance.map(({ node }) => (
                  <ListItem key={node.id}>
                    <span>{node.title}</span>
                    <Toggle
                      defaultOn={getOn(node)}
                      adornment={<Alert size={16} />}
                      onToggle={on => handleToggle(on, node)}
                    />
                  </ListItem>
                ))}
              </ul>
            </>
          ) : null}
        </DialogBody>
      </DialogWindow>
    </>
  )
}

TicketAlertsDialog.defaultProps = {
  alerts: [],
  types: {
    entrance: [],
    nonEntrance: [],
    groups: [],
  },
  onClose: () => {},
  onToggle: () => {},
}

TicketAlertsDialog.propTypes = {
  eventId: PropTypes.string.isRequired,
  alerts: PropTypes.array,
  types: PropTypes.shape({
    entrance: PropTypes.array,
    nonEntrance: PropTypes.array,
    groups: PropTypes.array,
  }),
  onClose: PropTypes.func,
  onToggle: PropTypes.func,
}

export default TicketAlertsDialog
